import { Graphics, GraphicsContext } from "pixi.js";
import type { AttackLevel } from "./attack";

/** Darken a 0xRRGGBB color by a factor (0 = black, 1 = unchanged). */
function darkenColor(color: number, factor: number): number {
  const r = Math.round(((color >> 16) & 0xff) * factor);
  const g = Math.round(((color >> 8) & 0xff) * factor);
  const b = Math.round((color & 0xff) * factor);
  return (r << 16) | (g << 8) | b;
}

/**
 * Part de la couleur qu'une note de touche noire garde.
 *
 * Elle est assombrie pour que le roll rappelle le clavier qu'il surplombe :
 * une note noire vient d'une touche noire, et la texture des deux se répond.
 *
 * Mais elle est déjà plus étroite — c'est la largeur de sa touche —, et à
 * 0,45 le rappel devenait une relégation : deux signaux pour le même fait,
 * dont le second dit « moins importante » d'une note qui ne l'est pas. La
 * position sous le clavier suffit d'ailleurs à dire laquelle est laquelle ;
 * l'assombrissement n'est là que pour la texture.
 */
const BLACK_KEY_DARKEN = 0.7;
/** How much to mix toward white when the note is currently being correctly played. */
const HIT_WHITE_MIX = 0.55;

/** Mix a 0xRRGGBB color toward white (factor 0 = unchanged, 1 = pure white). */
function brightenColor(color: number, factor: number): number {
  const r = (color >> 16) & 0xff;
  const g = (color >> 8) & 0xff;
  const b = color & 0xff;
  const nr = Math.round(r + (255 - r) * factor);
  const ng = Math.round(g + (255 - g) * factor);
  const nb = Math.round(b + (255 - b) * factor);

  return (nr << 16) | (ng << 8) | nb;
}

export type NoteStyle = {
  fillColor: number;
  strokeColor: number;
  radius: number;
  /**
   * Draw the note as a **contour** instead of a solid rectangle.
   *
   * What the trace is for: the live-piano lane laid over the written notes,
   * so a player can see where their own strike fell against the one that was
   * asked for. As a filled rectangle it cannot do that job — measured against
   * the roll's ground (L = 0.0055), `--color-hand-live` (#AFB5C0, L = 0.4597,
   * 9.58:1) sits at **1.16:1 against `--color-hand-left`** (#2FD7D6,
   * L = 0.5406): the same object in luminance. Darkened for a black key it is
   * still 1.14:1 against the darkened left hand, and only 2.38:1 against the
   * roll — under the 3:1 floor. There is no third fill available on this
   * surface.
   *
   * So it stops being a fill. Same x, same width, same radius as the note it
   * comments on — a rim around a place, not another note in the chord.
   */
  outline?: boolean;
}

export type NoteGraphicProps = {
  x: number;
  y: number;
  width: number;
  height: number;
  style: NoteStyle;
  isBlackKey: boolean;
  /** True when the user is currently holding the matching key while the note crosses the playhead. */
  isHit: boolean;
  /**
   * La note est encore tenue : sa hauteur grandit à chaque image.
   *
   * Une géométrie qui change à chaque image n'a rien à faire dans le cache
   * partagé — elle y entrerait une fois par image et n'en ressortirait jamais
   * (voir `getSharedContext`). Elle se dessine dans le contexte propre du
   * `Graphics`, comme le fait n'importe quel `Graphics` ordinaire.
   */
  held?: boolean;
  /**
   * La note passe sous la tête de lecture et n'est dessinée que jusqu'à elle :
   * son bas est une coupe, pas son attaque. Il est droit, sans arrondi ni
   * bordure — arrondi, il avait l'air d'une note plus courte posée sur la
   * ligne plutôt que d'une note qui passe dessous.
   */
  cutBottom?: boolean;
  /**
   * La force de la frappe, en palier (`attack.ts`) : l'épaisseur de la bande
   * d'attaque. 0, ou absent, pour une note dont la vélocité ne dit rien.
   */
  attack?: AttackLevel;
}

/**
 * Cache of shared GraphicsContext objects, keyed by a composite string.
 * When multiple notes share the same (width, height, fillColor, strokeColor, radius),
 * they reuse the same GPU-uploaded geometry instead of each holding their own.
 *
 * This follows the PixiJS v8 recommendation: swap pre-built GraphicsContext
 * objects instead of calling clear() + rebuild per Graphics each frame.
 */
/**
 * Soft cap on the shared GraphicsContext cache.
 *
 * The cache key includes `width` in pixels, plus colors and radius — for a
 * dense song this can grow into the thousands. We never **destroy** entries
 * mid-session: a `GraphicsContext` may still be assigned (`graphic.context = ctx`)
 * to live `NoteGraphic` instances in the pool, and destroying it under their
 * feet makes Pixi crash on the next render with `t.instructions is null`,
 * which silently kills the whole canvas.
 *
 * Instead, when the cap is reached we simply stop caching new entries: the
 * `NoteGraphic` then draws into the context its `Graphics` owns (see
 * `getSharedContext`). The already-cached entries remain alive for the page
 * lifetime; teardown via `clearNoteContextCache()` disposes them at a moment
 * where no `Graphics` references them anymore.
 */
/**
 * How the trace draws itself, and why these four numbers.
 *
 * Contrast is measured against the roll's ground (#0a0a10, L = 0.0055):
 *
 * | part        | value       | composite      | ratio vs roll |
 * |-------------|-------------|----------------|---------------|
 * | fill        | alpha 0.10  | (28, 29, 31)   | 1.2 : 1       |
 * | stroke      | alpha 0.85  | (151, 156, 168)| 6.87 : 1      |
 *
 * The fill is not there to be seen. It exists so a contour two pixels tall
 * is still a **shape** rather than two hairlines, and so a strike stays
 * pickable as one object when the notes behind it are dense. The stroke is
 * the whole signal, and it clears 3:1 by more than a factor of two — which
 * is what lets it keep its full value on a black key, where a darkened fill
 * would fall to 2.38:1.
 *
 * The bottom edge is drawn a second time, half a pixel wider. A note falls,
 * so its bottom edge **is its attack** — the instant that was either in time
 * or not. Everything above it is how long the key stayed down, which is a
 * different question and a quieter one.
 */
const OUTLINE_FILL_ALPHA = 0.1;
const OUTLINE_STROKE_ALPHA = 0.85;
const OUTLINE_STROKE_WIDTH = 1;
const OUTLINE_ATTACK_WIDTH = 1.5;

/**
 * La bande d'attaque : le bas d'une note, d'autant plus épais qu'elle a été
 * frappée fort.
 *
 * Le bas, parce qu'une note tombe : son bord bas est son attaque, celui que la
 * trace redessine déjà plus épais. Le reste de la note garde la couleur de sa
 * main et continue de dire la durée.
 *
 * Pas la luminosité du corps : elle dit déjà trois choses — touche noire
 * (`BLACK_KEY_DARKEN`), note en train d'être jouée (`HIT_WHITE_MIX`), note hors
 * sélection (alpha 0,55 dans `Notes`). Une note douce assombrie se lirait comme
 * une touche noire, et sur une touche noire la main droite n'est déjà qu'à
 * 1,7:1 contre le fond.
 *
 * Épaisseur par palier, en px, de rien (palier 0) à ff. Jamais plus de
 * `ATTACK_BAND_MAX_SHARE` de la hauteur : une note brève reste une note, et pas
 * une bande.
 *
 * De la couleur du liseré (`RIM_WHITE_MIX`), et c'est le même objet : le bord
 * bas d'une note *est* son attaque, la bande n'en est que l'épaississement
 * quand la frappe a été forte. Plus sombre — ce qu'elle était — elle ouvrait
 * un trou sous chaque note, et le liseré du bas courait par-dessus un fond
 * qui n'était plus celui du corps : deux traits pour un seul bord. Une seule
 * couleur, et la note s'allume par en bas.
 *
 * Tirée vers le blanc à 0,5, elle perdait sa teinte — c'était la raison de
 * l'assombrissement. Au mélange du liseré elle la garde, et sur touche noire
 * elle suit le liseré dans son assombrissement (voir `rimFor`) : la note y est
 * plus sombre en entier, attaque comprise.
 */
const ATTACK_BAND_PX = [0, 1.5, 3, 4.5, 6] as const;
const ATTACK_BAND_MAX_SHARE = 0.4;

/**
 * Le trait d'attaque de la trace, par palier. Jamais plus fin que les 1,5 px
 * d'avant : plus fin, il ne se distinguerait plus du contour.
 */
const OUTLINE_ATTACK_PX = [OUTLINE_ATTACK_WIDTH, OUTLINE_ATTACK_WIDTH, 2, 3, 4] as const;

/**
 * Le liseré lumineux : le bord de la note, sa couleur tirée vers le blanc.
 *
 * Avant, le contour était la couleur du corps à 0,35 — sur un fond noir, un
 * bord *plus sombre* que ce qu'il entoure : la note s'éteignait sur ses
 * quatre côtés au lieu de se détacher. Tiré vers le blanc, le même trait dit
 * l'inverse, et il le dit là où ça compte : mesuré contre le fond du rouleau
 * (#0a0a10), la main droite est à 6,83:1 quand son liseré (#C294FF) est à
 * 8,44:1. La note garde sa couleur, son bord porte le contraste. Sur touche
 * noire, c'est `rimFor` qui décide — et le raisonnement y change d'ordre.
 *
 * 0,18 et non les 0,28 de la maquette : à 0,28 le bord se voyait plus que la
 * note qu'il borde — sur le rouleau en défilement, la main droite se lisait
 * comme une grille de contours clairs plutôt que comme des notes violettes.
 * Le corps a monté depuis (voir `--color-hand-right`) : c'est lui qui porte
 * la couleur, le liseré n'a plus à la porter pour lui. Baissé, il reste très au-dessus du plancher de 3:1 et
 * continue de porter le bord ; il cesse d'être ce qu'on regarde. L'alpha
 * descend avec lui (0,85) : le corps transparaît un peu, et le trait perd son
 * tranchant sans perdre sa place.
 *
 * Posé à l'intérieur (`alignment: 1`) : dehors, il aurait mangé les deux
 * pixels d'écart entre deux touches voisines et deux notes contiguës se
 * seraient touchées.
 */
const RIM_WHITE_MIX = 0.18;
const RIM_ALPHA = 0.85;
const RIM_WIDTH = 1;

/**
 * Le liseré d'une note, touche noire comprise.
 *
 * L'ordre des deux opérations n'est pas indifférent, et c'est tout l'objet de
 * cette fonction. Éclaircir le corps *déjà assombri* donnait un liseré à
 * 1,93:1 de son corps sur une touche noire, contre 1,56:1 sur une blanche :
 * le bord ressortait le plus là où la note est la plus sombre, et sur le
 * rouleau on ne voyait plus que le contour des notes noires.
 *
 * Assombrir le liseré de la note claire donne le même rapport des deux côtés
 * — les deux couleurs sont multipliées par le même facteur, donc leur écart
 * relatif ne bouge pas (1,44:1). Une touche noire, c'est la note entière plus
 * sombre, liseré compris, et non une note sombre bordée de clair.
 *
 * Le liseré garde malgré tout son office de contraste : la main droite sur
 * touche noire (#7E57B2) est à 3,66:1 contre le fond du rouleau, et son
 * liseré (#8767B2) à 4,34:1. Le corps y tenait sous le plancher de 3:1
 * (2,68:1) tant que la main droite était le violet de la maquette.
 */
function rimFor(color: number, isBlackKey: boolean): number {
  const rim = brightenColor(color, RIM_WHITE_MIX);

  return isBlackKey ? darkenColor(rim, BLACK_KEY_DARKEN) : rim;
}

/**
 * Sous cette hauteur, pas de liseré : à deux pixels, un trait d'un pixel posé
 * en dedans ne borde plus rien — il remplace la note.
 */
const RIM_MIN_HEIGHT_PX = 4;

/**
 * Le débord : le liseré continué hors du bord, à peine visible.
 *
 * C'est lui qui fait « lumineuse » plutôt que « claire » — une bordure nette
 * reste un trait, un trait qui déteint sur le fond est une lumière.
 *
 * Deux pixels, pas trois, et en deux anneaux d'opacités décroissantes : un
 * trait de Pixi a les bords durs, et une seule passe large posait autour de
 * chaque note un cadre net — une deuxième bordure, plus épaisse et plus
 * terne que celle qu'on voulait fine. Empilés, les deux anneaux font une
 * dégressivité (0,30 puis 0,10 contre le fond) et la note reste bordée d'un
 * seul pixel franc.
 */
const GLOW_RINGS = [
  { width: 2, alpha: 0.1 },
  { width: 1, alpha: 0.22 },
] as const;

const CONTEXT_CACHE_MAX = 1024;

/**
 * Pas des pixels pour la clé : le pas de la grille sur laquelle on arrondit.
 *
 * Une hauteur en pixels est un flottant, et deux notes de durées voisines
 * donnaient deux entrées. Arrondies au demi-pixel — invisible sur un
 * rectangle de quelques pixels — elles partagent la même géométrie, et le
 * cache se remplit avec le nombre de durées *distinctes* du morceau, pas
 * avec le nombre de notes.
 */
const CACHE_GRID_PX = 0.5;

const contextCache = new Map<string, GraphicsContext>();

function snapToGrid(value: number): number {
  return Math.round(value / CACHE_GRID_PX) * CACHE_GRID_PX;
}

/**
 * Peint la bande d'attaque par-dessus le fond : les `band` pixels du bas de la
 * note, coins arrondis compris.
 *
 * Moins haute que le rayon, son bord supérieur coupe les deux arcs : elle part
 * de leur intersection et non du bord, sans quoi ses coins dépasseraient de la
 * note.
 */
function paintAttackBand(
  target: GraphicsContext,
  width: number,
  height: number,
  radius: number,
  band: number,
  color: number,
): GraphicsContext {
  // Le rayon que Pixi pose réellement : jamais plus que la moitié du plus petit
  // côté.
  const r = Math.min(radius, width / 2, height / 2);
  const top = height - band;

  if (r <= 0) {
    return target.rect(0, top, width, band).fill({ color, alpha: 1 });
  }

  if (band >= r) {
    return target
      .moveTo(0, top)
      .lineTo(0, height - r)
      .arc(r, height - r, r, Math.PI, Math.PI / 2, true)
      .lineTo(width - r, height)
      .arc(width - r, height - r, r, Math.PI / 2, 0, true)
      .lineTo(width, top)
      .closePath()
      .fill({ color, alpha: 1 });
  }

  const sweep = Math.asin((r - band) / r);

  return target
    .moveTo(r - r * Math.cos(sweep), top)
    .arc(r, height - r, r, Math.PI - sweep, Math.PI / 2, true)
    .lineTo(width - r, height)
    .arc(width - r, height - r, r, Math.PI / 2, sweep, true)
    .closePath()
    .fill({ color, alpha: 1 });
}

function buildContext(
  width: number,
  height: number,
  fillColor: number,
  strokeColor: number,
  rimColor: number,
  radius: number,
  outline: boolean,
  cutBottom: boolean,
  attack: AttackLevel,
  target: GraphicsContext,
): GraphicsContext {
  if (cutBottom && !outline) {
    const r = Math.min(radius, width / 2, height);

    // Le fond sur la forme fermée, la bordure sur trois côtés seulement : sur
    // la coupe, un trait dessinerait un bord que la note n'a pas.
    return target
      .moveTo(0, height)
      .lineTo(0, r)
      .arcTo(0, 0, r, 0, r)
      .lineTo(width - r, 0)
      .arcTo(width, 0, width, r, r)
      .lineTo(width, height)
      .closePath()
      .fill({ color: fillColor, alpha: 1 })
      .moveTo(0, height)
      .lineTo(0, r)
      .arcTo(0, 0, r, 0, r)
      .lineTo(width - r, 0)
      .arcTo(width, 0, width, r, r)
      .lineTo(width, height)
      .stroke({ color: rimColor, alpha: RIM_ALPHA, width: RIM_WIDTH });
  }

  if (outline) {
    const attackWidth = OUTLINE_ATTACK_PX[attack];
    const attackY = height - attackWidth / 2;

    return target
      .roundRect(0, 0, width, height, radius)
      .fill({ color: fillColor, alpha: OUTLINE_FILL_ALPHA })
      .stroke({ color: strokeColor, alpha: OUTLINE_STROKE_ALPHA, width: OUTLINE_STROKE_WIDTH })
      .moveTo(radius, attackY)
      .lineTo(Math.max(radius, width - radius), attackY)
      .stroke({ color: strokeColor, alpha: OUTLINE_STROKE_ALPHA, width: attackWidth });
  }

  // L'ordre est celui de la profondeur : le débord sous le fond — sans quoi il
  // voilerait la note —, la bande d'attaque par-dessus le fond, et le liseré
  // en dernier, pour qu'il fasse le tour entier. Peint avant elle, son bord
  // bas passait sous la bande et la note n'était bordée que sur trois côtés.
  for (const ring of GLOW_RINGS) {
    target
      .roundRect(0, 0, width, height, radius)
      .stroke({ color: rimColor, alpha: ring.alpha, width: ring.width, alignment: 0 });
  }

  const body = target
    .roundRect(0, 0, width, height, radius)
    .fill({ color: fillColor, alpha: 1 });
  const band = Math.min(ATTACK_BAND_PX[attack], height * ATTACK_BAND_MAX_SHARE);

  if (band > 0) {
    paintAttackBand(body, width, height, radius, band, rimColor);
  }

  if (height < RIM_MIN_HEIGHT_PX) {
    return body;
  }

  return body
    .roundRect(0, 0, width, height, radius)
    .stroke({ color: rimColor, alpha: RIM_ALPHA, width: RIM_WIDTH, alignment: 1 });
}

/**
 * Le contexte partagé pour cette géométrie, ou `null` si le cache est plein
 * et qu'il n'y est pas.
 *
 * Avant, un cache plein rendait quand même un contexte neuf — non partagé,
 * jamais détruit : chaque note au-delà des 1024 premières géométries
 * laissait derrière elle un `GraphicsContext` et sa copie GPU. En jeu libre,
 * où chaque durée est unique, c'était une fuite d'une géométrie par note (et
 * d'une par image tant qu'elle était tenue). Rendre `null` renvoie l'appelant
 * vers le contexte propre de son `Graphics`, qui est réutilisé et détruit avec
 * lui.
 */
function getSharedContext(
  width: number,
  height: number,
  fillColor: number,
  strokeColor: number,
  rimColor: number,
  radius: number,
  outline: boolean,
  cutBottom: boolean,
  attack: AttackLevel,
): GraphicsContext | null {
  // Le liseré est dans la clé : il ne se déduit pas du reste. Une même couleur
  // de bord peut venir d'une note de touche blanche ou de l'assombrissement
  // d'une autre, et les deux n'ont pas le même liseré (voir `rimFor`).
  const key = `${width}|${height}|${fillColor}|${strokeColor}|${rimColor}|${radius}|${outline ? 't' : 'n'}|${cutBottom ? 'c' : 'w'}|${attack}`;

  const existing = contextCache.get(key);

  if (existing) {
    return existing;
  }

  if (contextCache.size >= CONTEXT_CACHE_MAX) {
    return null;
  }

  const ctx = buildContext(width, height, fillColor, strokeColor, rimColor, radius, outline, cutBottom, attack, new GraphicsContext());
  contextCache.set(key, ctx);

  return ctx;
}

/** Flush the shared context cache (call on destroy / scene teardown only). */
export function clearNoteContextCache(): void {
  for (const ctx of contextCache.values()) {
    ctx.destroy();
  }
  contextCache.clear();
}

/** Taille du cache partagé, pour les tests. */
export function noteContextCacheSize(): number {
  return contextCache.size;
}

/**
 * A single note in the piano roll.
 */
export class NoteGraphic {
  public readonly graphic: Graphics;
  /**
   * Le contexte que le `Graphics` possède (créé et détruit avec lui). Il sert
   * aux géométries qui n'ont pas leur place dans le cache partagé : la note
   * tenue, et tout ce qui arrive quand le cache est plein.
   */
  private readonly ownContext: GraphicsContext;

  // Ce qui a été dessiné la dernière fois, en scalaires : l'objet `style`
  // passé en entrée est réutilisé d'une image à l'autre par l'appelant, en
  // garder la référence comparerait la nouvelle valeur à elle-même.
  private drawn = false;
  private prevWidth = 0;
  private prevHeight = 0;
  private prevBlack = false;
  private prevHit = false;
  private prevFill = 0;
  private prevStroke = 0;
  private prevRadius = 0;
  private prevOutline = false;
  private prevCut = false;
  private prevAttack: AttackLevel = 0;

  constructor() {
    this.graphic = new Graphics();
    this.ownContext = this.graphic.context;
  }

  draw(props: NoteGraphicProps): void {
    const { x, y, style, isBlackKey, isHit } = props;
    const outline = style.outline === true;
    const held = props.held === true;
    const cutBottom = props.cutBottom === true;
    // Une note coupée a laissé son attaque sous la ligne : plus de bande à
    // peindre, et une seule géométrie quelle que soit sa force.
    const attack = cutBottom ? 0 : props.attack ?? 0;
    // Une note tenue se redessine à chaque image de toute façon : inutile
    // d'arrondir. Les autres s'alignent sur la grille du cache pour que deux
    // durées voisines partagent la même géométrie.
    const width = held ? props.width : snapToGrid(props.width);
    const height = held ? props.height : snapToGrid(props.height);

    if (this.shouldRedraw(width, height, style, isBlackKey, isHit, outline, cutBottom, attack)) {
      // A contour takes neither of the two colour treatments a note takes.
      // Darkening it for a black key would put it at 2.38:1 against the roll,
      // under the floor — and the trace is not "a note on a key", it is the
      // mark of a strike, worth the same wherever it landed. Brightening it
      // on a hit would be worse: `isHit` means *this* note is being played
      // right now, and the trace is a record of a pass that is over.
      const fill = outline || !isBlackKey ? style.fillColor : darkenColor(style.fillColor, BLACK_KEY_DARKEN);
      const edge = outline || !isBlackKey ? style.strokeColor : darkenColor(style.strokeColor, BLACK_KEY_DARKEN);

      const fillColor = !outline && isHit ? brightenColor(fill, HIT_WHITE_MIX) : fill;
      const strokeColor = !outline && isHit ? 0xffffff : edge;
      const rimColor = !outline && isHit ? 0xffffff : rimFor(style.strokeColor, isBlackKey);

      const shared = held
        ? null
        : getSharedContext(width, height, fillColor, strokeColor, rimColor, style.radius, outline, cutBottom, attack);

      if (shared !== null) {
        this.graphic.context = shared;
      } else {
        this.ownContext.clear();
        buildContext(width, height, fillColor, strokeColor, rimColor, style.radius, outline, cutBottom, attack, this.ownContext);
        this.graphic.context = this.ownContext;
      }

      this.drawn = true;
      this.prevWidth = width;
      this.prevHeight = height;
      this.prevBlack = isBlackKey;
      this.prevHit = isHit;
      this.prevFill = style.fillColor;
      this.prevStroke = style.strokeColor;
      this.prevRadius = style.radius;
      this.prevOutline = outline;
      this.prevCut = cutBottom;
      this.prevAttack = attack;
    }

    this.graphic.x = x;
    this.graphic.y = y;
    this.graphic.visible = true;
  }

  private shouldRedraw(
    width: number,
    height: number,
    style: NoteStyle,
    isBlackKey: boolean,
    isHit: boolean,
    outline: boolean,
    cutBottom: boolean,
    attack: AttackLevel,
  ): boolean {
    if (!this.drawn) {
      return true;
    }

    return (
      cutBottom !== this.prevCut
      || attack !== this.prevAttack
      || width !== this.prevWidth
      || height !== this.prevHeight
      || isBlackKey !== this.prevBlack
      || isHit !== this.prevHit
      || style.fillColor !== this.prevFill
      || style.strokeColor !== this.prevStroke
      || style.radius !== this.prevRadius
      || outline !== this.prevOutline
    );
  }

  hide(): void {
    this.graphic.visible = false;
  }

  destroy(): void {
    // Le contexte propre part avec le `Graphics` ; un contexte partagé
    // appartient au cache, qui le détruit lui-même à `clearNoteContextCache`.
    this.graphic.context = this.ownContext;
    this.graphic.destroy();
  }
}
